import {
    type FieldError,
    type Control,
    type FieldPath,
    type FieldValues,
    useController,
} from 'react-hook-form';
import Input, { type InputProps as BaseInputProps } from '@repo/input';

export interface ControlledInputProps<TFieldValues extends FieldValues = FieldValues> extends Omit<BaseInputProps, 'error' | 'onChange' | 'onBlur' | 'name' | 'ref' | 'value' | 'defaultValue'> {
    name: FieldPath<TFieldValues>;
    control: Control<TFieldValues>;
    error?: FieldError;
    className?: string;
    /** false — не показывать ошибку (для визарда до нажатия «Далее»/submit) */
    showError?: boolean;
    /** Подмена значения при вводе (например маска телефона) до записи в react-hook-form. */
    formatOnChange?: (value: string) => string;
    /** Значение, записываемое в форму при очистке числового поля. */
    emptyNumberValue?: null | undefined;
}

export default function ControlledInput<TFieldValues extends FieldValues = FieldValues>({
    name,
    control,
    error: errorProp,
    showError = true,
    className = '',
    disabled,
    formatOnChange,
    emptyNumberValue,
    ...props
}: ControlledInputProps<TFieldValues>) {
    const { field, fieldState } = useController({
        control,
        name,
        disabled,
    });

    const error = showError ? fieldState.error || errorProp : undefined;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (props.type === 'number') {
            const numValue = parseFloat(event.target.value);
            field.onChange(
                event.target.value === ''
                    ? emptyNumberValue
                    : (isNaN(numValue) ? emptyNumberValue : numValue),
            );
            return;
        }
        const raw = event.target.value;
        field.onChange(formatOnChange ? formatOnChange(raw) : raw);
    };

    const handleBlur = () => {
        field.onBlur();
    };

    const displayValue = props.type === 'number'
        ? (field.value === undefined || field.value === null ? '' : String(field.value))
        : (field.value ?? '');

    return (
        <Input
            className={className}
            error={error?.message}
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            {...props}
        />
    );
}
